import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subdomainService, LandingPage, Subdomain } from '../services/subdomainService';
import { usePageTitle } from '../hooks/usePageTitle';

interface PublicLandingPageProps {
  subdomain: string;
  slug: string;
}

const PublicLandingPage: React.FC<PublicLandingPageProps> = ({ subdomain, slug }) => {
  usePageTitle('Landing Page');
  const navigate = useNavigate();
  const [landingPage, setLandingPage] = useState<LandingPage | null>(null);
  const [subdomainData, setSubdomainData] = useState<Subdomain | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadLandingPage = useCallback(async () => {
    if (!subdomain || !slug) return;

    try {
      setLoading(true);
      setError('');

      // Load subdomain data
      const subdomainInfo = await subdomainService.getSubdomainBySubdomain(subdomain);
      if (!subdomainInfo || !subdomainInfo.isActive) {
        setError('Subdomain not found or inactive');
        return;
      }
      setSubdomainData(subdomainInfo);

      // Load landing page
      const page = await subdomainService.getLandingPageBySlug(subdomain, slug);
      if (!page) {
        setError('Landing page not found');
        return;
      }
      setLandingPage(page);

      // Record page view
      await subdomainService.recordPageView(subdomain, slug);
    } catch (err) {
      console.error('Error loading landing page:', err);
      setError('Failed to load landing page');
    } finally {
      setLoading(false);
    }
  }, [subdomain, slug]);

  useEffect(() => {
    if (subdomain && slug) {
      loadLandingPage();
    }
  }, [subdomain, slug, loadLandingPage]);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!landingPage || !subdomain) return;

    try {
      setSubmitting(true);

      // Validate required fields
      const requiredFields = landingPage.formFields.filter(field => field.required);
      for (const field of requiredFields) {
        if (!formData[field.id]?.trim()) {
          setError(`Please fill in the ${field.label} field`);
          return;
        }
      }

      // Here you would typically send the form data to your backend
      // For now, we'll just simulate a successful submission
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Record conversion
      await subdomainService.recordConversion(subdomain, landingPage.slug);
      
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!landingPage) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: landingPage.styling.backgroundColor }}>
      {/* Custom CSS */}
      {landingPage.content.css && (
        <style dangerouslySetInnerHTML={{ __html: landingPage.content.css }} />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* SEO Meta Tags */}
          {landingPage.settings.seoTitle && (
            <title>{landingPage.settings.seoTitle}</title>
          )}
          {landingPage.settings.seoDescription && (
            <meta name="description" content={landingPage.settings.seoDescription} />
          )}

          {/* Landing Page Content */}
          <div 
            className="bg-white rounded-lg shadow-lg overflow-hidden"
            style={{ 
              borderRadius: landingPage.styling.borderRadius,
              fontFamily: landingPage.styling.fontFamily 
            }}
          >
            {submitted ? (
              <div className="p-8 text-center">
                <div className="text-green-600 text-6xl mb-4">✅</div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
                <p className="text-gray-600 mb-6">
                  Your submission has been received successfully.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Submit Another Response
                </button>
              </div>
            ) : (
              <div className="p-8">
                {/* Page Title */}
                <h1 
                  className="text-3xl font-bold mb-6"
                  style={{ color: landingPage.styling.textColor }}
                >
                  {landingPage.title}
                </h1>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {landingPage.formFields.map((field) => (
                    <div key={field.id}>
                      <label 
                        htmlFor={field.id}
                        className="block text-sm font-medium mb-2"
                        style={{ color: landingPage.styling.textColor }}
                      >
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <input
                        type={field.type === 'email' ? 'email' : 'text'}
                        id={field.id}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                        placeholder={field.placeholder || field.label}
                        required={field.required}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        style={{ 
                          borderRadius: landingPage.styling.borderRadius,
                          fontFamily: landingPage.styling.fontFamily 
                        }}
                      />
                    </div>
                  ))}

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-800 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 px-6 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                    style={{
                      backgroundColor: landingPage.styling.primaryColor,
                      color: 'white',
                      borderRadius: landingPage.styling.borderRadius
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Subscribe'}
                  </button>
                </form>

                {/* Social Links */}
                {subdomainData?.customDomain && (
                  <div className="mt-8 text-center">
                    <p className="text-sm text-gray-500 mb-4">Follow us on social media</p>
                    <div className="flex justify-center space-x-4">
                      {/* Add social media links here */}
                    </div>
                  </div>
                )}

                {/* Powered by Scubamail */}
                <div className="mt-8 text-center">
                  <p className="text-xs text-gray-400">
                    Powered by{' '}
                    <span className="font-medium" style={{ color: landingPage.styling.primaryColor }}>
                      Scubamail
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom JavaScript */}
      {landingPage.content.js && (
        <script dangerouslySetInnerHTML={{ __html: landingPage.content.js }} />
      )}

      {/* Analytics */}
      {landingPage.settings.analyticsId && (
        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${landingPage.settings.analyticsId}`}
        />
      )}
      {landingPage.settings.analyticsId && (
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${landingPage.settings.analyticsId}');
            `
          }}
        />
      )}
    </div>
  );
};

export default PublicLandingPage;
